import React, { useState, useEffect, useCallback, useRef } from 'react';
import { feature } from 'topojson-client';
import Header from './components/Header';
import UtilityMap from './components/UtilityMap';
import UtilityScorecard from './components/UtilityScorecard';

function App() {
  // Index data (lightweight - utility names, available years, national averages)
  const [indexData, setIndexData] = useState(null);
  // Year-specific data (loaded on demand)
  const [yearDataCache, setYearDataCache] = useState({});
  // Territory data
  const [territoriesData, setTerritoriesData] = useState(null);
  const [statesData, setStatesData] = useState(null);
  // Loading states
  const [loading, setLoading] = useState(true);
  const [yearLoading, setYearLoading] = useState(false);
  const [error, setError] = useState(null);

  // Selection state
  const [selectedUtilityId, setSelectedUtilityId] = useState(null);
  const [selectedYear, setSelectedYear] = useState('2024');
  const [selectedMetric, setSelectedMetric] = useState('residential_cents_kwh');

  // Available years
  const [availableYears, setAvailableYears] = useState([]);

  // Ref for triggering map zoom
  const mapRef = useRef(null);

  // Load year data on demand
  const loadYearData = useCallback(async (year) => {
    if (yearDataCache[year]) {
      return yearDataCache[year];
    }

    setYearLoading(true);
    try {
      const response = await fetch(`/data/years/utilities_${year}.json`);
      if (!response.ok) {
        throw new Error(`Failed to load data for ${year}`);
      }
      const data = await response.json();
      setYearDataCache(prev => ({ ...prev, [year]: data }));
      setYearLoading(false);
      return data;
    } catch (err) {
      console.error(`Error loading year ${year}:`, err);
      setYearLoading(false);
      return null;
    }
  }, [yearDataCache]);

  // Load initial data with staggered fetches for faster perceived load
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Phase 1: Load lightweight files first (allows map to render with states)
        // Using TopoJSON for 60%+ smaller file sizes
        const [indexResponse, statesResponse] = await Promise.all([
          fetch('/data/utilities_index.json'),
          fetch('/data/territories/us_states.topojson')
        ]);

        if (!indexResponse.ok) {
          throw new Error('Failed to load utilities index');
        }

        const index = await indexResponse.json();
        setIndexData(index);

        // Extract available years (filter out 2012 which has no data)
        const years = (index.available_years || []).filter(y => y !== '2012');
        setAvailableYears(years);
        const latestYear = years.length > 0 ? years[years.length - 1] : '2024';
        setSelectedYear(latestYear);

        // Set states data - convert TopoJSON to GeoJSON for Mapbox
        if (statesResponse.ok) {
          const statesTopo = await statesResponse.json();
          const statesGeoJSON = feature(statesTopo, statesTopo.objects.states);
          setStatesData(statesGeoJSON);
        }

        // Allow initial render with map outline
        setLoading(false);

        // Phase 2: Load larger files in background
        const [yearResponse, territoriesResponse] = await Promise.all([
          fetch(`/data/years/utilities_${latestYear}.json`),
          fetch('/data/territories/utility_territories.topojson')
        ]);

        if (yearResponse.ok) {
          const yearData = await yearResponse.json();
          setYearDataCache({ [latestYear]: yearData });
        }

        // Convert TopoJSON to GeoJSON for Mapbox
        if (territoriesResponse.ok) {
          const territoriesTopo = await territoriesResponse.json();
          const territoriesGeoJSON = feature(territoriesTopo, territoriesTopo.objects.utilities);
          setTerritoriesData(territoriesGeoJSON);
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Load year data when year changes
  useEffect(() => {
    if (selectedYear && !yearDataCache[selectedYear]) {
      loadYearData(selectedYear);
    }
  }, [selectedYear, yearDataCache, loadYearData]);

  // Load ALL years for selected utility (for trend graphs)
  useEffect(() => {
    if (selectedUtilityId && indexData) {
      const utility = indexData.utilities[selectedUtilityId];
      if (utility?.available_years) {
        // Load all years for this utility that aren't already cached
        utility.available_years.forEach(year => {
          if (!yearDataCache[year]) {
            loadYearData(year);
          }
        });
      }
    }
  }, [selectedUtilityId, indexData, yearDataCache, loadYearData]);

  // Build utilities data object compatible with existing components
  const utilitiesData = indexData ? {
    utilities: Object.fromEntries(
      Object.entries(indexData.utilities).map(([uid, udata]) => [
        uid,
        {
          ...udata,
          years: Object.fromEntries(
            (udata.available_years || []).map(year => [
              year,
              yearDataCache[year]?.[uid] || null
            ]).filter(([_, data]) => data !== null)
          )
        }
      ])
    ),
    national_averages: indexData.national_averages,
    summary: indexData.summary,
    metadata: indexData.metadata
  } : null;

  // Get selected utility data
  const getUtilityData = (utilityId) => {
    if (!utilitiesData || !utilityId) return null;
    return utilitiesData.utilities[utilityId] || null;
  };

  // Get national averages for selected year
  const getNationalAverages = () => {
    if (!indexData) return null;
    return indexData.national_averages[selectedYear] || null;
  };

  // Handle utility selection from map or search
  const handleUtilitySelect = useCallback((utilityId, shouldZoom = false) => {
    setSelectedUtilityId(utilityId);
    // If selected from search, trigger zoom
    if (shouldZoom && mapRef.current) {
      mapRef.current.zoomToUtility(utilityId);
    }
  }, []);

  // Handle search selection (always zoom)
  const handleSearchSelect = useCallback((utilityId) => {
    setSelectedUtilityId(utilityId);
    // Trigger zoom after a short delay to ensure state is updated
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.zoomToUtility(utilityId);
      }
    }, 100);
  }, []);

  // Clear selection
  const handleClearSelection = () => {
    setSelectedUtilityId(null);
  };

  // No longer blocking with loading spinner - map renders immediately
  // while data loads progressively in the background

  if (error) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gray-100">
        <div className="text-center max-w-md p-6 bg-white rounded-lg shadow">
          <div className="text-red-500 text-5xl mb-4">!</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">
            Please ensure the data pipeline has been run and files exist in public/data/
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden bg-gray-100">
      {/* Header */}
      <Header
        selectedYear={selectedYear}
        onYearChange={setSelectedYear}
        selectedMetric={selectedMetric}
        onMetricChange={setSelectedMetric}
        availableYears={availableYears}
        summary={indexData?.summary}
        utilities={indexData?.utilities}
        onUtilitySelect={handleSearchSelect}
      />

      {/* Loading indicators */}
      {loading && (
        <div className="bg-gray-100 text-gray-600 text-center py-1 text-sm flex items-center justify-center gap-2">
          <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
          Loading utilities data...
        </div>
      )}
      {!loading && yearLoading && (
        <div className="bg-blue-500 text-white text-center py-1 text-sm">
          Loading {selectedYear} data...
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Map - 2/3 width */}
        <div className="w-2/3 h-full">
          <UtilityMap
            ref={mapRef}
            territoriesData={territoriesData}
            statesData={statesData}
            utilitiesData={utilitiesData}
            yearDataCache={yearDataCache}
            selectedYear={selectedYear}
            selectedMetric={selectedMetric}
            selectedUtilityId={selectedUtilityId}
            onUtilitySelect={handleUtilitySelect}
          />
        </div>

        {/* Scorecard - 1/3 width */}
        <div className="w-1/3 h-full flex flex-col bg-white border-l border-gray-200 overflow-hidden">
          {!selectedUtilityId ? (
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center text-gray-500">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                <p className="text-lg font-medium">Select a utility</p>
                <p className="text-sm mt-2">Click on any region on the map or use the search bar</p>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-auto">
              <UtilityScorecard
                utilityId={selectedUtilityId}
                utilityData={getUtilityData(selectedUtilityId)}
                nationalAverages={getNationalAverages()}
                selectedYear={selectedYear}
                onClose={handleClearSelection}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
